import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, Link as RouterLink } from 'react-router-dom'
import {
    FormattedMessage as _FormattedMessage,
    useIntl,
    defineMessages
} from 'react-intl'
import {
    useSetPageTitle,
    captureException,
    useGetFeatureConfig,
    getFeatureConfig
} from '@thriveglobal/thrive-web-core'
import {
    CoreAlert,
    CoreTypography,
    useTheme
} from '@thriveglobal/thrive-web-leafkit'
import { Grid, Grow, Link, Box, AlertTitle, Typography } from '@mui/material'
import { ROUTES } from '../../routes'
import {
    GetIdpConfigsForUserByEmailQuery,
    IdpConfig,
    useGetIdpConfigsForUserByEmailLazyQuery
} from '../../graphql/generated/autogenerated'
import {
    processIdpResponse,
    findSsoConfig,
    findCredentialConfig,
    findAzureConfig,
    filterUsersIdps,
    supportedIdpTypes,
    findMagicLinkConfig
} from '../../utils/handleIdpConfig'
import ErrorAlert, {
    ErrorAlertType
} from '../../components/elements/ErrorAlert'
import useQuery from '../../hooks/useQuery'
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form'
import useTokenExchange from '../../POC/useTokenExchange'
import { addEmailDomainToLoggingPlatform } from '../../utils/setEmailDomainInLoggingPlatform'
import { shouldForceSso } from '../../utils/shouldForceSso'
import SignInMethodDivider from '../../components/elements/SignInMethodDivider'
import LandingPageMainCta from './LandingPageMainCta'
import LandingPageSignInOptions, {
    signInButtonOption
} from './LandingPageSignInOptions'
import EmailSignInForm from './EmailSignInForm'
import useSignInWithPassword from '../../hooks/useSignInWithPassword'
import useSignInWithSso from '../../hooks/useSignInWithSso'
import useSignInWithMicrosoft from '../../hooks/useSignInWithMicrosoft'
import { Avo } from '@thriveglobal/thrive-web-tracking'
import { ModifiedFormattedMessageType } from '../../types'

const FormattedMessage =
    _FormattedMessage as unknown as ModifiedFormattedMessageType

interface SignInStatusMessage {
    active?: boolean
    severity?: 'info' | 'error' | 'warning'
    heading?: string
    message?: string
}

interface CustomContactSupportMessage {
    heading?: string
    message?: string
}

interface SigninProps {
    onEmailChange: (email: string) => void
    handleReferrer: (referrerId: string) => void
}

interface ISigninFormInputs {
    email: string
    password?: string
}

const messages = defineMessages({
    pageTitle: {
        defaultMessage: 'Thrive - Sign In',
        description: 'Login page title'
    },
    loginGreeting: {
        description: 'Login greeting',
        defaultMessage: "It's Time <br /> To Thrive"
    }
})

function getCompanyId(idpResponse: GetIdpConfigsForUserByEmailQuery): string {
    return idpResponse?.getIdpConfigsForUserByEmail?.items?.[0]?.companyId
}

interface GetButtonsToShowParamsType {
    showPasswordField: boolean
    showSsoButton: boolean
    showMicrosoftButton: boolean
}

type getButtonsToShowResponse =
    | 'microsoft'
    | 'sso'
    | 'continue'
    | 'passwordLogin'
function getButtonsToShow({
    showPasswordField,
    showSsoButton,
    showMicrosoftButton
}: GetButtonsToShowParamsType): Array<getButtonsToShowResponse> {
    const buttons = []
    if (!showPasswordField && !showSsoButton && !showMicrosoftButton) {
        return ['continue']
    }

    if (showPasswordField) {
        buttons.push('passwordLogin')
    }

    if (showSsoButton) {
        buttons.push('sso')
    }

    if (showMicrosoftButton) {
        buttons.push('microsoft')
    }

    return buttons
}

function getLandingPageOptions(
    buttonsToShow: Array<getButtonsToShowResponse>
): Array<signInButtonOption> {
    if (!buttonsToShow) {
        return []
    }
    const [, ...options] = buttonsToShow as Array<signInButtonOption>
    return options
}

const LandingPage: React.FC<SigninProps> = ({
    onEmailChange,
    handleReferrer
}) => {
    const [email, setEmail] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    const [showPasswordField, setShowPasswordField] = useState<boolean>(false)
    const [showSsoButton, setShowSsoButton] = useState<boolean>(false)
    const [showMicrosoftButton, setShowMicrosoftButton] =
        useState<boolean>(false)
    const passwordInputRef = React.useRef<HTMLInputElement>()
    const [passwordValidationError, setPasswordValidationError] =
        useState<boolean>(false)
    const [eligibilityValidationError, setEligibilityValidationError] =
        useState<boolean>(false)
    const [inviteRevokedError, setInviteRevokedError] = useState<boolean>(false)

    const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false)
    const [errorType, setErrorType] = useState<ErrorAlertType>('DEFAULT')
    const [signInLoading, setSignInLoading] = useState<boolean>(false)
    const [showContactSupportMessage, setShowContactSupportMessage] =
        useState<boolean>(false)
    const [customContactSupportMessage, setCustomContactSupportMessage] =
        useState<null | CustomContactSupportMessage>(null)

    //clear any referrerId from state if user navigates to login page from a referral link
    useEffect(() => {
        handleReferrer('')
    }, [])

    const history = useHistory()
    const theme = useTheme()
    const intl = useIntl()

    const navigateToLandingPageSelect = useCallback(() => {
        onEmailChange(email.toLowerCase())
        history.push(ROUTES.LANDING_STEP_2)
    }, [onEmailChange, email, history])

    const [getIdpConfigs, { loading: idpLoading, data: idpData, error }] =
        useGetIdpConfigsForUserByEmailLazyQuery({
            fetchPolicy: 'network-only'
        })

    useEffect(() => {
        if (idpData) {
            const processIdpData = async () => {
                const forceSso = shouldForceSso({
                    idpConfigs: idpData.getIdpConfigsForUserByEmail.items,
                    email
                })
                if (forceSso) {
                    signInWithSSO()
                    return
                }
                processIdpResponse({
                    idpData,
                    noIdpCallback: async () => {
                        const userIdpTypes =
                            idpData?.getIdpConfigsForUserByEmail?.userIdpTypes
                                ?.length
                        if (userIdpTypes > 0) {
                            setShowContactSupportMessage(true)
                            try {
                                const companyId = getCompanyId(idpData)
                                const customContactSupportMessageConfig =
                                    await getFeatureConfig<CustomContactSupportMessage>(
                                        'customContactThriveSupportMessage',
                                        null,
                                        false,
                                        { companyId }
                                    )
                                if (customContactSupportMessageConfig) {
                                    setCustomContactSupportMessage(
                                        customContactSupportMessageConfig
                                    )
                                }
                            } catch (error) {
                                captureException(error, {
                                    message:
                                        'Unable to set custom contact support message'
                                })
                            }
                            return
                        }
                        routeToBrandSelect()
                    },
                    multipleIdpCallback: async () => {
                        const userIdps = filterUsersIdps(
                            idpData?.getIdpConfigsForUserByEmail?.items,
                            idpData?.getIdpConfigsForUserByEmail?.userIdpTypes
                        )

                        const magicLinkConfig = findMagicLinkConfig(userIdps)
                        if (magicLinkConfig) {
                            navigateToLandingPageSelect()
                            return
                        }

                        const credentialConfig = findCredentialConfig({
                            idpConfigs: userIdps
                        })
                        if (credentialConfig) {
                            showPassword()
                        }

                        await showSsoButtons(userIdps)
                    },
                    singleIdpCallback: async () => {
                        const userIdps = filterUsersIdps(
                            idpData?.getIdpConfigsForUserByEmail?.items,
                            idpData?.getIdpConfigsForUserByEmail?.userIdpTypes
                        )

                        const magicLinkConfig = findMagicLinkConfig(userIdps)
                        if (magicLinkConfig) {
                            navigateToLandingPageSelect()
                            return
                        }
                        const credentialConfig = findCredentialConfig({
                            idpConfigs: userIdps
                        })
                        if (credentialConfig) {
                            showPassword()
                        }
                    },
                    email
                })
            }
            processIdpData()
        }
    }, [idpData])

    const urlSearch = useQuery()
    useTokenExchange()

    const formMethods = useForm<ISigninFormInputs>({
        defaultValues: { email: '', password: '' }
    })
    const { handleSubmit } = formMethods

    const PAGE_TITLE = intl.formatMessage(messages.pageTitle)
    useSetPageTitle(PAGE_TITLE)

    const buttonsToShow = useMemo(
        () =>
            getButtonsToShow({
                showPasswordField,
                showSsoButton,
                showMicrosoftButton
            }),
        [showPasswordField, showSsoButton, showMicrosoftButton]
    )

    const landingPageOptions: Array<signInButtonOption> = useMemo(
        () => getLandingPageOptions(buttonsToShow),
        [buttonsToShow]
    )

    useEffect(() => {
        const error = urlSearch.get('error')
        const errorDescription = urlSearch.get('error_description')
        if (error || errorDescription) {
            setShowErrorAlert(true)
            captureException(new Error('Error on redirect to sign in app'), {
                message: `Sign In Loading Error. error: ${error}, error_description: ${errorDescription}`
            })
            if (error === 'MagicLinkError') {
                setErrorType('MAGIC_LINK')
            }
        }
    }, [])

    useEffect(() => {
        if (error) {
            captureException(error, { message: 'Failed to fetch idp configs' })
            Avo.signinFailed({
                activityType: 'user_signin',
                featureType: 'identity',
                method: 'credentials',
                reason: 'Failed to fetch idp configs'
            })
            setShowErrorAlert(true)
        }
    }, [error])

    const handleEmailChange = (newValue: string) => {
        const newEmail = newValue.trim()

        const emailChanged = newEmail !== email
        if (showPasswordField && emailChanged) {
            setShowPasswordField(false)
            setShowSsoButton(false)
            setShowMicrosoftButton(false)
        }
        setEmail(newEmail)
    }

    const signInStatusMessage: SignInStatusMessage =
        useGetFeatureConfig('signInStatusMessage', null, true) || {}

    const routeToBrandSelect = () => {
        onEmailChange(email.toLowerCase())
        history.push(ROUTES.BRAND_SELECT)
    }

    const setShowSSOButtonToTrue = () => {
        setShowSsoButton(true)
    }

    const setShowMicrosoftButtonToTrue = () => {
        setShowMicrosoftButton(true)
    }

    const showSsoButtons = async (userIdps: Array<IdpConfig>) => {
        const ssoConfig = findSsoConfig(userIdps)
        if (ssoConfig) {
            setShowSSOButtonToTrue()
        }
        const azureConfig = findAzureConfig(userIdps)
        if (azureConfig) {
            setShowMicrosoftButtonToTrue()
        }
    }

    const showPassword = () => {
        setShowPasswordField(true)
        passwordInputRef.current?.focus()
    }

    const signInWithPassword = useSignInWithPassword({
        idpConfigs: idpData?.getIdpConfigsForUserByEmail?.items,
        email,
        password,
        setShowErrorAlert,
        passwordInputRef,
        setPasswordValidationError,
        setEligibilityValidationError,
        setInviteRevokedError
    })

    const signInWithSSO = useSignInWithSso({
        idpConfigs: idpData?.getIdpConfigsForUserByEmail?.items,
        setShowErrorAlert
    })

    const signInWithMicrosoft = useSignInWithMicrosoft({
        idpConfigs: idpData?.getIdpConfigsForUserByEmail?.items,
        email,
        setShowErrorAlert
    })

    const handleIdpFetch = async () => {
        getIdpConfigs({
            variables: {
                email,
                onlyForExistingIdpProfiles: false,
                idpConfigFilters: {
                    idpTypeAllowlist: supportedIdpTypes,
                    enabled: true
                }
            }
        })
    }

    const onMainCtaClick: SubmitHandler<ISigninFormInputs> = async () => {
        if (idpLoading || signInLoading) {
            return
        }
        setPasswordValidationError(false)
        setShowErrorAlert(false)
        const mainCtaButton = buttonsToShow[0]

        if (showPasswordField) {
            setSignInLoading(true)
            const signInSuccess = await signInWithPassword()
            setSignInLoading(false)
            if (!signInSuccess) {
                setShowErrorAlert(true)
            }
            return
        } else if (mainCtaButton === 'continue') {
            addEmailDomainToLoggingPlatform(email)
            handleIdpFetch()
        } else if (mainCtaButton === 'sso') {
            await signInWithSSO()
        }
    }

    return (
        <FormProvider {...formMethods}>
            <form
                data-testid="login"
                id="login-form"
                noValidate
                onSubmit={handleSubmit(onMainCtaClick)}
            >
                <Grid container spacing={2}>
                    {signInStatusMessage?.active && (
                        <CoreAlert severity={signInStatusMessage.severity}>
                            {signInStatusMessage.heading && (
                                <AlertTitle>
                                    <Typography variant="h4" component="p">
                                        {signInStatusMessage.heading}
                                    </Typography>
                                </AlertTitle>
                            )}
                            <Typography variant="body1">
                                {signInStatusMessage.message}
                            </Typography>
                        </CoreAlert>
                    )}
                    <Grid item xs={12}>
                        <CoreTypography customVariant="display1" component="h1">
                            <FormattedMessage
                                defaultMessage="Sign In <br></br> To Thrive"
                                description="Login page header"
                                values={{
                                    br: () => <br />
                                }}
                            />
                        </CoreTypography>
                        <CoreTypography
                            variant="body1"
                            component="p"
                            sx={{ mt: 3 }}
                        >
                            <FormattedMessage
                                defaultMessage="Don't have an account yet?  <Link>Sign up now.</Link>"
                                description="Call to action to get new users to sign up"
                                values={{
                                    Link: (chunks) => (
                                        <Link
                                            to={ROUTES.BRAND_SELECT}
                                            component={RouterLink}
                                        >
                                            <CoreTypography
                                                component="span"
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.primary
                                                        .contrastText,
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {chunks}
                                            </CoreTypography>
                                        </Link>
                                    )
                                }}
                            />
                        </CoreTypography>
                        <ErrorAlert
                            show={showErrorAlert}
                            errorType={errorType}
                        />
                    </Grid>
                    <EmailSignInForm
                        onEmailChange={handleEmailChange}
                        onPasswordChange={setPassword}
                        showPasswordField={showPasswordField}
                        passwordInputRef={passwordInputRef}
                        passwordValidationError={passwordValidationError}
                        eligibilityValidationError={eligibilityValidationError}
                        inviteRevokedError={inviteRevokedError}
                    />
                    <Grid item xs={12}>
                        <Grow
                            in={showContactSupportMessage}
                            style={{ transformOrigin: '0 0 0' }}
                            {...(showContactSupportMessage
                                ? { timeout: 1000 }
                                : {})}
                            mountOnEnter
                            unmountOnExit
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    [theme.breakpoints.down('sm')]: {
                                        width: '100%'
                                    }
                                }}
                            >
                                {!customContactSupportMessage && (
                                    <CoreAlert severity="warning">
                                        <AlertTitle>
                                            <Typography
                                                variant="h4"
                                                component="p"
                                            >
                                                <FormattedMessage
                                                    description="Title of alert to let user know they need to contact Thrive support"
                                                    defaultMessage="Contact support"
                                                />
                                            </Typography>
                                        </AlertTitle>
                                        <Typography variant="body1">
                                            <FormattedMessage
                                                description="Title of alert to let user know they need to contact Thrive support"
                                                defaultMessage="We are unable to find an up to date method for you to sign into Thrive with. If you think this may be an error, please reach out to <emailLink>support@thriveglobal.com</emailLink>"
                                                values={{
                                                    emailLink: (chunk) => (
                                                        <Link
                                                            href={`mailto:support@thriveglobal.com`}
                                                            color="primary.dark"
                                                        >
                                                            {chunk}
                                                        </Link>
                                                    )
                                                }}
                                            />
                                        </Typography>
                                    </CoreAlert>
                                )}
                                {customContactSupportMessage && (
                                    <CoreAlert severity="warning">
                                        <AlertTitle>
                                            <Typography
                                                variant="h4"
                                                component="p"
                                            >
                                                {
                                                    customContactSupportMessage.heading
                                                }
                                            </Typography>
                                        </AlertTitle>
                                        <Typography variant="body1">
                                            {
                                                customContactSupportMessage.message
                                            }
                                        </Typography>
                                    </CoreAlert>
                                )}
                            </Box>
                        </Grow>
                    </Grid>

                    <Grid item xs={12}>
                        <LandingPageMainCta
                            loading={idpLoading || signInLoading}
                            showMicrosoftButton={showMicrosoftButton}
                            showPasswordField={showPasswordField}
                            showSsoButton={showSsoButton}
                        />
                    </Grid>
                    <Grow
                        in={buttonsToShow.length > 1}
                        style={{ transformOrigin: '0 0 0' }}
                        {...(buttonsToShow.length > 1 ? { timeout: 1000 } : {})}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    maxWidth: '36rem',
                                    marginBottom: '1rem'
                                }}
                            >
                                <SignInMethodDivider />
                            </Box>
                            <LandingPageSignInOptions
                                buttonsToShow={landingPageOptions}
                                signInWithMicrosoft={signInWithMicrosoft}
                                signInWithSSO={signInWithSSO}
                            />
                        </Grid>
                    </Grow>
                </Grid>
            </form>
        </FormProvider>
    )
}

export default LandingPage
